export interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  audience: string[];
  tags: string[];
  relatedLinks?: {
    text: string;
    url: string;
  }[];
}

export const faqCategories = [
  { id: 'getting-started', name: 'Getting Started', icon: 'RocketLaunchIcon' },
  { id: 'core-features', name: 'Core Features', icon: 'StarIcon' },
  { id: 'thrivecircle-family', name: 'ThriveCircle & Family', icon: 'UserGroupIcon' },
  { id: 'community-social', name: 'Community & Social', icon: 'ChatBubbleLeftRightIcon' },
  { id: 'chat-companions', name: 'Chat Companions', icon: 'SparklesIcon' },
  { id: 'plans-billing', name: 'Plans & Billing', icon: 'CreditCardIcon' },
  { id: 'technical-support', name: 'Technical Support', icon: 'WrenchScrewdriverIcon' },
  { id: 'privacy-security', name: 'Privacy & Security', icon: 'ShieldCheckIcon' },
  { id: 'professional-services', name: 'Professional Services', icon: 'BriefcaseIcon' },
  { id: 'background-checks', name: 'Caregiver Background Check & Pre‑Check FAQs', icon: 'DocumentCheckIcon' },
  { id: 'safety-emergency', name: 'Safety & Emergency', icon: 'ExclamationTriangleIcon' },
  // Consolidated categories for older-adult support page
  { id: 'using-seniorthrive', name: 'Using SeniorThrive', icon: 'StarIcon' },
  { id: 'family-sharing', name: 'Family & Sharing', icon: 'UserGroupIcon' },
  { id: 'account-billing', name: 'Account & Billing', icon: 'CreditCardIcon' },
  { id: 'safety-privacy', name: 'Safety & Privacy', icon: 'ShieldCheckIcon' },
];

export const faqs: FAQ[] = [
  // Getting Started
  {
    id: 'how-to-get-started',
    question: 'How do I get started with SeniorThrive?',
    answer: "Getting started is simple! Sign up for your free 7-day ThriveMax trial, complete the Quick Health Check to get your ThriveScore, and take your first ThriveVision room scan. Our chat companion Avery will walk you through each step and help you create your personalized action plan.",
    category: 'getting-started',
    audience: ['older-adults', 'families'],
    tags: ['onboarding', 'trial', 'setup', 'avery', 'thrivemax'],
    relatedLinks: [
      { text: 'Get ThriveMax', url: '/pricing' },
      { text: 'How It Works Guide', url: '/features' }
    ]
  },
  {
    id: 'what-is-thrivescore',
    question: 'What is my ThriveScore and how is it calculated?',
    answer: "Your ThriveScore is a personalized wellness metric that combines your health status, home safety assessment, and activity levels into one easy-to-understand number. It's calculated using your health check responses, room scan results, and daily activity tracking. Your score updates as you make improvements and complete recommended actions.",
    category: 'getting-started',
    audience: ['older-adults', 'families'],
    tags: ['thrivescore', 'wellness', 'assessment']
  },
  {
    id: 'free-trial-details',
    question: "What's included in the free 7-day trial?",
    answer: "Your free trial includes full access to all ThriveMax features: unlimited room scans with ThriveVision, complete health tracking, the ability to invite one family member, all six chat companions (Avery, Clarity, Stanley, Riley, Finn, and Charlie), personalized action plans, and ThriveCircle coordination. No credit card required to start!",
    category: 'getting-started',
    audience: ['older-adults', 'families'],
    tags: ['trial', 'features', 'pricing', 'chat-companions', 'thrivemax']
  },
  {
    id: 'what-is-thrivemax',
    question: 'What is ThriveMax?',
    answer: "ThriveMax is the core plan for older adults who want to thrive at home. It includes everything you need: health and wellness tracking, unlimited home safety scans, medication management, personal ThriveScore, calendar sync, daily living assessments, The Village community access, built-in video calls, the ability to invite one family member, and all six chat companions. ThriveMax is $19/month after your free 7-day trial.",
    category: 'getting-started',
    audience: ['older-adults', 'families'],
    tags: ['thrivemax', 'plans', 'pricing', 'older-adults']
  },

  // Core Features
  {
    id: 'thrivevision-how-it-works',
    question: 'How does ThriveVision room scanning work?',
    answer: "ThriveVision analyzes photos of your rooms to identify potential fall risks and safety hazards. Simply take a photo with your phone or tablet, and ThriveVision will highlight areas of concern like loose rugs, poor lighting, or clutter, then provide specific recommendations to make each space safer. Clarity, our Home Safety chat companion, can provide additional insights and answer follow-up questions about your scan results.",
    category: 'core-features',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['thrivevision', 'safety', 'fall-prevention', 'clarity']
  },
  {
    id: 'daily-checkin-purpose',
    question: 'What is the Daily Check-in and why is it important?',
    answer: "The Daily Check-in is a quick wellness assessment that helps track your health trends and mood over time. It takes just 2-3 minutes and helps us personalize your recommendations while giving your family peace of mind about your daily well-being.",
    category: 'core-features',
    audience: ['older-adults', 'families'],
    tags: ['daily-checkin', 'health-tracking', 'wellness']
  },
  {
    id: 'action-plans-explained',
    question: 'How do personalized action plans work?',
    answer: "Based on your ThriveScore, room scans, and health check results, we create a customized action plan with specific, achievable steps to improve your safety and wellness. Each recommendation includes why it matters, how to implement it, and links to products or services that can help.",
    category: 'core-features',
    audience: ['older-adults', 'families'],
    tags: ['action-plans', 'recommendations', 'personalization']
  },

  // ThriveCircle & Family
  {
    id: 'invite-family-members',
    question: 'How do I invite family members to my ThriveCircle?',
    answer: "In your dashboard, click 'Manage ThriveCircle' and enter your family member's email address. They'll receive an invitation to create their account and connect with yours. You can control what information they can see and set different permission levels for each family member. Riley, our Relationship & Care chat companion, can help guide you through family conversations about care coordination.",
    category: 'thrivecircle-family',
    audience: ['older-adults', 'families'],
    tags: ['thrivecircle', 'family', 'invitations', 'permissions', 'riley']
  },
  {
    id: 'family-dashboard-features',
    question: 'What can family members see on their dashboard?',
    answer: "Family members can view your ThriveScore trends, recent room scan results (if you choose to share), daily check-in summaries, and any safety recommendations. They can also coordinate care tasks, schedule check-ins, and receive notifications about important updates with your permission.",
    category: 'thrivecircle-family',
    audience: ['families', 'caregivers'],
    tags: ['family-dashboard', 'permissions', 'coordination']
  },
  {
    id: 'thrivefamily-explained',
    question: 'What is ThriveFamily and how is it different from ThriveMax?',
    answer: "ThriveFamily ($49/month) is designed for families who want to support their older loved one. It includes everything in ThriveMax for the older adult, plus a shared ThriveCircle dashboard, the ability to invite up to 4 family members, shared calendar and coordination tools, and family check-in features. ThriveMax includes only 1 family member slot. With ThriveFamily, the whole family can stay connected and coordinated.",
    category: 'thrivecircle-family',
    audience: ['families'],
    tags: ['thrivefamily', 'plans', 'billing', 'coordination']
  },
  {
    id: 'thrivefamily-plus-explained',
    question: 'What is ThriveFamily+?',
    answer: "ThriveFamily+ (+$10/month added to ThriveFamily) is for families doing hands-on caregiving who need deeper coordination tools. It includes condition-specific care packs (for dementia, diabetes, heart health, and more), visit scheduling, incident reporting, voice notes for care documentation, care handoffs, supply requests, care load tracking, and self-care reminders for caregivers. It becomes available when families need more intensive coordination.",
    category: 'thrivecircle-family',
    audience: ['families'],
    tags: ['thrivefamily-plus', 'care-tools', 'coordination', 'caregiving']
  },
  {
    id: 'thrivecare-addon-explained',
    question: 'What is the ThriveCare Add-On?',
    answer: "The ThriveCare Add-On (+$15/caregiver/month) allows families using ThriveFamily to invite professional caregivers into their ThriveCircle. Caregivers get access to care delivery tools, voice notes, incident logging, and care handoffs—all within the family's existing system. Caregivers don't purchase their own plans; they're invited by families who add this to their subscription.",
    category: 'thrivecircle-family',
    audience: ['families', 'caregivers'],
    tags: ['thrivecare', 'caregivers', 'add-on', 'professional']
  },

  // Chat Companions
  {
    id: 'chat-companions-overview',
    question: 'What chat companions are available in SeniorThrive?',
    answer: "SeniorThrive includes six specialized chat companions: Avery (your all-around platform guide), Clarity (home safety expert), Stanley (medication assistant), Riley (relationship & care coach), Finn (feline companion for joy and comfort), and Charlie (canine buddy for encouragement). Each companion has unique expertise and personality traits designed to support different aspects of your independent living journey.",
    category: 'chat-companions',
    audience: ['older-adults', 'families'],
    tags: ['chat-companions', 'avery', 'clarity', 'stanley', 'riley', 'finn', 'charlie', 'overview']
  },
  {
    id: 'avery-platform-guide',
    question: 'How does Avery help me navigate SeniorThrive?',
    answer: "Avery is your comprehensive SeniorThrive expert who helps you understand and use all platform features. Whether you need help interpreting your ThriveScore, understanding scan results, or finding specific tools, Avery provides friendly, knowledgeable guidance. She's especially helpful during onboarding and when exploring new features.",
    category: 'chat-companions',
    audience: ['older-adults', 'families'],
    tags: ['avery', 'platform-guide', 'onboarding', 'navigation']
  },
  {
    id: 'clarity-safety-expert',
    question: 'What can Clarity tell me about my home safety?',
    answer: "Clarity specializes in home safety and fall prevention. She can explain your ThriveVision scan results in detail, provide specific safety recommendations for each room, suggest products and modifications, and answer questions about creating a safer home environment. Clarity combines practical safety knowledge with encouraging guidance.",
    category: 'chat-companions',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['clarity', 'home-safety', 'fall-prevention', 'thrivevision']
  },
  {
    id: 'stanley-medication-help',
    question: 'How does Stanley help with my medications?',
    answer: "Stanley (PharmaThrive) is your medication management assistant who can answer questions about drug interactions, side effects, and prescription management. He helps you understand how medications might affect balance or well-being, provides reminders about adherence, and offers guidance on organizing your medication routine safely.",
    category: 'chat-companions',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['stanley', 'pharmathrive', 'medications', 'drug-interactions', 'prescription-management']
  },
  {
    id: 'riley-relationship-coach',
    question: 'What makes Riley different from other chat companions?',
    answer: "Riley specializes in relationship and care coordination, focusing on the emotional and interpersonal aspects of aging at home. She helps navigate difficult family conversations, provides support for care coordination challenges, and offers empathetic guidance for both older adults and family caregivers. Riley excels at helping families communicate effectively about care needs.",
    category: 'chat-companions',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['riley', 'relationship-coach', 'family-communication', 'care-coordination']
  },
  {
    id: 'finn-charlie-emotional-support',
    question: 'How do Finn and Charlie provide emotional support?',
    answer: "Finn (our Feline Friend) and Charlie (our Canine Buddy) bring joy and companionship to your daily routine. Finn offers gentle, curious interactions and can share information about cats and pet adoption. Charlie provides energetic encouragement and motivation for staying active. Both companions help combat loneliness and add delightful moments to your day.",
    category: 'chat-companions',
    audience: ['older-adults', 'families'],
    tags: ['finn', 'charlie', 'emotional-support', 'companionship', 'pets']
  },
  {
    id: 'choosing-chat-companions',
    question: 'Can I choose which chat companion to talk to?',
    answer: "Yes! You can select which chat companion you'd like to interact with based on your current needs. Need help with the platform? Choose Avery. Have safety questions? Talk to Clarity. Medication concerns? Stanley is your go-to. Want emotional support? Riley, Finn, or Charlie are ready to help. You can switch between companions anytime.",
    category: 'chat-companions',
    audience: ['older-adults', 'families'],
    tags: ['chat-companions', 'selection', 'interaction', 'personalization']
  },
  {
    id: 'chat-companions-learning',
    question: 'Do the chat companions learn my preferences?',
    answer: "Yes, our chat companions gradually learn your preferences, communication style, and specific needs over time. They remember your previous conversations and adapt their responses to be more helpful and personalized. This creates a more natural, supportive experience as you continue using SeniorThrive.",
    category: 'chat-companions',
    audience: ['older-adults', 'families'],
    tags: ['chat-companions', 'personalization', 'learning', 'adaptation']
  },

  // Plans & Billing
  {
    id: 'pricing-plans-explained',
    question: 'What are the different pricing plans?',
    answer: "We offer five options: ThriveStarter (free with limited features), ThriveMax ($19/month - the core plan for older adults), ThriveFamily ($49/month - for families supporting a loved one), ThriveFamily+ (+$10/month - for families doing hands-on caregiving), and ThriveCare Add-On (+$15/caregiver/month - for inviting professional caregivers). Start with a free 7-day ThriveMax trial to experience full features.",
    category: 'plans-billing',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['pricing', 'plans', 'billing', 'thrivemax', 'thrivefamily']
  },
  {
    id: 'after-trial-ends',
    question: 'What happens when my free trial ends?',
    answer: "When your 7-day trial ends, you can choose to subscribe to ThriveMax to continue with full features, or your account will automatically switch to ThriveStarter (free plan) with limited features. You'll never lose your data, and you can upgrade anytime.",
    category: 'plans-billing',
    audience: ['older-adults', 'families'],
    tags: ['trial', 'billing', 'subscription', 'thrivemax']
  },
  {
    id: 'family-billing-when-starts',
    question: 'When does billing start for ThriveFamily?',
    answer: "ThriveFamily billing starts when an older adult joins your ThriveCircle. You can explore and set up the platform for free, and you won't be charged until your loved one accepts the invitation and activates their account. This way, you can prepare everything before committing.",
    category: 'plans-billing',
    audience: ['families'],
    tags: ['thrivefamily', 'billing', 'trial']
  },
  {
    id: 'can-family-sponsor',
    question: 'Can family members pay for their loved one\'s subscription?',
    answer: "Yes! Family members can sponsor the subscription for their older loved one. With ThriveFamily, billing is managed centrally by the family, and the older adult gets full ThriveMax access as part of the family plan.",
    category: 'plans-billing',
    audience: ['families'],
    tags: ['billing', 'family', 'sponsor', 'thrivefamily']
  },

  // Technical Support
  {
    id: 'device-compatibility',
    question: 'What devices work with SeniorThrive?',
    answer: "SeniorThrive works on any device with a web browser - smartphones, tablets, laptops, and desktop computers. We recommend using Chrome, Safari, Firefox, or Edge browsers for the best experience. The platform is designed to be senior-friendly with large text and simple navigation.",
    category: 'technical-support',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['compatibility', 'devices', 'browsers']
  },
  {
    id: 'trouble-taking-photos',
    question: "I'm having trouble taking photos for room scans. What should I do?",
    answer: "Make sure your browser has camera permissions enabled for SeniorThrive. Take photos in good lighting, hold your device steady, and try to capture the whole room. If you're still having issues, you can upload photos from your device's photo gallery instead. Clarity, our Home Safety chat companion, can also provide tips for getting the best scan results.",
    category: 'technical-support',
    audience: ['older-adults', 'families'],
    tags: ['camera', 'photos', 'troubleshooting', 'clarity']
  },
  {
    id: 'forgot-password',
    question: 'I forgot my password. How do I reset it?',
    answer: "On the login page, click 'Forgot Password' and enter your email address. We'll send you a secure link to create a new password. If you don't see the email, check your spam folder or contact our support team for help.",
    category: 'technical-support',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['password', 'login', 'account']
  },

  // Privacy & Security
  {
    id: 'data-security',
    question: 'How is my personal information protected?',
    answer: "Your data is protected with bank-level encryption and stored securely in HIPAA-compliant servers. We never sell your personal information, and you control who in your ThriveCircle can see your health data. All photos are processed securely and can be deleted at any time.",
    category: 'privacy-security',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['privacy', 'security', 'hipaa', 'encryption']
  },
  {
    id: 'sharing-permissions',
    question: 'Can I control what my family sees?',
    answer: "Absolutely! You have complete control over what information is shared with your ThriveCircle. You can set different permission levels for each family member and change these settings anytime in your privacy preferences.",
    category: 'privacy-security',
    audience: ['older-adults', 'families'],
    tags: ['permissions', 'privacy', 'family-sharing']
  },

  // Safety & Emergency
  {
    id: 'emergency-features',
    question: 'What emergency features does SeniorThrive offer?',
    answer: "SeniorThrive includes emergency contact management, medication tracking with critical alerts, and integration with local emergency services information. While we don't replace medical alert systems, we help ensure your emergency information is organized and accessible to your ThriveCircle.",
    category: 'safety-emergency',
    audience: ['older-adults', 'families', 'caregivers'],
    tags: ['emergency', 'safety', 'medical-alert']
  },
  {
    id: 'fall-risk-recommendations',
    question: 'What should I do if ThriveVision identifies fall risks in my home?',
    answer: "Review the specific recommendations provided for each identified risk. We prioritize suggestions by safety impact and provide links to products and local services that can help. Start with the highest-priority items and work through the list at your own pace. Clarity, our Home Safety chat companion, can provide additional guidance and answer specific questions about implementing safety improvements.",
    category: 'safety-emergency',
    audience: ['older-adults', 'families'],
    tags: ['fall-prevention', 'safety', 'recommendations', 'clarity']
  },

  // Professional Services
  {
    id: 'caregiver-access',
    question: 'How do professional caregivers access SeniorThrive?',
    answer: "Professional caregivers access SeniorThrive through family invitations. When a family using ThriveFamily adds the ThriveCare Add-On, they can invite their caregiver into the ThriveCircle. Caregivers then get access to care delivery tools, documentation features, and coordination capabilities—all within the family's system. There are no standalone professional plans; caregivers work within family-led ThriveCircles.",
    category: 'professional-services',
    audience: ['caregivers', 'families'],
    tags: ['caregivers', 'thrivecare', 'professional-access', 'invitation']
  },
  {
    id: 'find-local-services',
    question: 'Can SeniorThrive help me find local care services?',
    answer: "Yes! Based on your location and needs assessment, we can connect you with verified local services including home safety specialists, certified aging-in-place specialists (CAPS), occupational therapists, and other senior care professionals in your area.",
    category: 'professional-services',
    audience: ['older-adults', 'families'],
    tags: ['local-services', 'professionals', 'referrals']
  },

  // Caregiver Background Check & Pre-Check FAQs
  {
    id: 'prechecked-badge-meaning',
    question: 'What is the Pre‑Checked badge on a caregiver profile?',
    answer: "It's a verification badge that shows a caregiver has completed a background check through our trusted partner. It helps families quickly see which caregivers have taken extra steps to stand out.",
    category: 'background-checks',
    audience: ['families', 'caregivers'],
    tags: ['background-check', 'pre-checked', 'verification', 'badge']
  },
  {
    id: 'who-runs-background-check',
    question: 'Who runs the background check?',
    answer: "All checks are processed by BackgroundChecks.com, a nationally accredited Consumer Reporting Agency. SeniorThrive does not run, store, or share the full reports.",
    category: 'background-checks',
    audience: ['families', 'caregivers'],
    tags: ['background-check', 'backgroundchecks.com', 'consumer-reporting-agency']
  },
  {
    id: 'how-caregiver-gets-prechecked',
    question: 'How does a caregiver get Pre‑Checked?',
    answer: "Caregivers click Get Pre‑Checked in their dashboard, create their own secure BackgroundChecks.com account, and order the check. Once complete, the badge appears on their SeniorThrive profile automatically.",
    category: 'background-checks',
    audience: ['caregivers'],
    tags: ['background-check', 'pre-checked', 'process', 'dashboard']
  },
  {
    id: 'what-info-seniorthrive-sees',
    question: 'What information does SeniorThrive see?',
    answer: "We only see the status — whether the check is complete or still processing. The full report remains private in the caregiver's BackgroundChecks.com account.",
    category: 'background-checks',
    audience: ['families', 'caregivers'],
    tags: ['background-check', 'privacy', 'information', 'status']
  },
  {
    id: 'who-pays-background-check',
    question: 'Who pays for the background check?',
    answer: "Caregivers pay directly through BackgroundChecks.com. Pricing and package details are shown before purchase.",
    category: 'background-checks',
    audience: ['caregivers'],
    tags: ['background-check', 'payment', 'pricing', 'caregiver-cost']
  },
  {
    id: 'background-check-timeline',
    question: 'How long does the process take?',
    answer: "Most checks finish within 1–3 business days. Some county or federal searches may take longer depending on local records.",
    category: 'background-checks',
    audience: ['families', 'caregivers'],
    tags: ['background-check', 'timeline', 'processing-time']
  },
  {
    id: 'families-agencies-background-checks',
    question: 'Can families or hiring agencies run background checks too?',
    answer: "Yes. Families or agencies can also create their own BackgroundChecks.com account and run a check on a caregiver before hiring.",
    category: 'background-checks',
    audience: ['families', 'caregivers'],
    tags: ['background-check', 'families', 'agencies', 'hiring']
  },

  // Community & Social
  {
    id: 'community-features',
    question: 'How can I connect with other SeniorThrive users?',
    answer: "SeniorThrive includes The Village community features where you can connect with other older adults and family caregivers, share tips and success stories, participate in wellness challenges, and find support. Finn and Charlie often facilitate community interactions around pet-related topics, while Riley can help with family caregiver support groups.",
    category: 'community-social',
    audience: ['older-adults', 'families'],
    tags: ['community', 'social', 'support', 'the-village', 'finn', 'charlie', 'riley']
  },
  // Getting Started — Older Adults
  {
    id: 'oa-credit-card',
    question: 'Do I need a credit card to sign up?',
    answer: 'No. You can create your account and start the free 7-day ThriveMax trial without entering any payment information. If you decide to subscribe after the trial, you can add a payment method at that time.',
    category: 'getting-started',
    audience: ['older-adults'],
    tags: ['signup', 'billing', 'trial', 'credit-card']
  },
  {
    id: 'oa-phone-tablet',
    question: 'Can I use SeniorThrive on my phone or tablet?',
    answer: 'Yes. SeniorThrive works on any device with a web browser, including smartphones, tablets, laptops, and desktop computers. The interface is designed with large text and simple navigation so it is comfortable to use on any screen size.',
    category: 'getting-started',
    audience: ['older-adults'],
    tags: ['devices', 'mobile', 'tablet', 'compatibility']
  },
  {
    id: 'oa-after-trial-data',
    question: 'What happens to my information if I don\'t subscribe after the trial?',
    answer: 'Your data is never deleted. If you choose not to subscribe, your account automatically switches to ThriveStarter (free) with limited features. You can still view your past scan results and assessments, and you can upgrade to full access anytime.',
    category: 'getting-started',
    audience: ['older-adults'],
    tags: ['trial', 'data', 'thrivestarter', 'privacy']
  },
  {
    id: 'oa-family-signup',
    question: 'Can my family member sign up for me?',
    answer: 'Yes. A family member can create an account on your behalf and even sponsor your subscription through the ThriveFamily plan. They can set everything up and then invite you to join when you are ready.',
    category: 'getting-started',
    audience: ['older-adults'],
    tags: ['family', 'signup', 'thrivefamily', 'sponsor']
  },
  {
    id: 'oa-help-during-setup',
    question: 'What if I need help during setup?',
    answer: 'Avery, your personal SeniorThrive guide, is available as soon as you log in. You can also reach our support team by phone, email, or live chat. We are here to help you get started with confidence.',
    category: 'getting-started',
    audience: ['older-adults'],
    tags: ['support', 'setup', 'avery', 'help'],
    relatedLinks: [
      { text: 'Contact Support', url: '/contact' }
    ]
  }
];

export const getPopularFAQs = () => {
  return faqs.filter(faq => 
    ['how-to-get-started', 'what-is-thrivemax', 'chat-companions-overview', 'thrivevision-how-it-works', 'thrivefamily-explained', 'pricing-plans-explained'].includes(faq.id)
  );
};

export const getFAQsByCategory = (categoryId: string) => {
  return faqs.filter(faq => faq.category === categoryId);
};

export const getFAQsByAudience = (audience: string) => {
  return faqs.filter(faq => faq.audience.includes(audience));
};

export const searchFAQs = (query: string) => {
  const lowercaseQuery = query.toLowerCase();
  return faqs.filter(faq => 
    faq.question.toLowerCase().includes(lowercaseQuery) ||
    faq.answer.toLowerCase().includes(lowercaseQuery) ||
    faq.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
  );
};
